import { getBehaviorObjectFieldOptions } from "../../../../util/src/blueprint/object-path-typing.js";
import { useBehaviorPathSchema } from "../BehaviorPathSchemaContext.js";
import {
  DATE_PART_TEXT_FORMAT_CHOICES,
  type DatePartTextFormat,
  type Port,
  type UnaryOperatorNode as UnaryOperatorNodeType,
  type VariableValueType,
} from "../../../../util/src/blueprint/types.js";
import { OperatorNodeCard } from "./OperatorNodeCard.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import React, { useCallback } from "react";

export interface UnaryOperatorNodeProps {
  node: UnaryOperatorNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  /**
   * When the user changes the date part text format for `getDay` or `getMonth`
   * (output becomes a localized string instead of a raw number).
   */
  onDatePartFormatChange?: (
    nodeId: string,
    format: DatePartTextFormat | undefined,
  ) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
  onObjectFieldPathChange?: (nodeId: string, fieldPath: string) => void;
}

function datePartFormatChoiceLabel(
  choice: DatePartTextFormat,
  labels: {
    numeric: string;
    narrow: string;
    short: string;
    long: string;
    twoDigit: string;
  },
): string {
  switch (choice) {
    case "numeric":
      return labels.numeric;
    case "narrow":
      return labels.narrow;
    case "short":
      return labels.short;
    case "long":
      return labels.long;
    case "2-digit":
      return labels.twoDigit;
    default:
      return choice;
  }
}

const UnaryOperatorNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onDatePartFormatChange,
  onObjectFieldPathChange,
}: UnaryOperatorNodeProps) => {
  const bpDico = dico().behavior.operators;
  const formatLabels = bpDico.datePartFormat;
  const isComparator = node.operator === "comparator";
  const outputPort = node.ports.find((p) => p.direction === "output");
  const inputPort = node.ports.find((p) => p.direction === "input");
  // `comparator` has TWO output ports (`outputA` and `outputB`).
  const comparatorPortA = isComparator
    ? node.ports.find((p) => p.id === "outputA")
    : undefined;
  const comparatorPortB = isComparator
    ? node.ports.find((p) => p.id === "outputB")
    : undefined;
  const showDatePartFormatSelect =
    (node.operator === "getDay" || node.operator === "getMonth") &&
    !!onDatePartFormatChange;
  const pathSchema = useBehaviorPathSchema(node.objectSource);
  const objectFieldOptions =
    node.operator === "get" && node.objectSource
      ? getBehaviorObjectFieldOptions(
          pathSchema,
          node.objectFieldPathBase,
          node.objectFieldPath,
        )
      : [];
  const showObjectFieldSelect =
    node.operator === "get" &&
    !!onObjectFieldPathChange &&
    objectFieldOptions.length > 0;

  const handleDatePartFormatSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (!onDatePartFormatChange) {
        return;
      }
      onDatePartFormatChange(node.id, event.target.value as DatePartTextFormat);
    },
    [node.id, onDatePartFormatChange],
  );

  if (!inputPort) {
    return null;
  }
  if (!isComparator && !outputPort) {
    return null;
  }
  if (isComparator && (!comparatorPortA || !comparatorPortB)) {
    return null;
  }

  const datePartSelectValue = node.datePartTextFormat ?? "numeric";

  const datePartLabelId = `date-part-format-${node.id}`;

  return (
    <OperatorNodeCard>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 6,
          paddingLeft: 4,
        }}
      >
        <PortHandle
          setPortsMap={setPortsMap}
          node={node}
          key={inputPort.id}
          port={inputPort}
          onMouseDown={() => onPortMouseDown?.(node.id, inputPort)}
          onMouseUp={() => onPortMouseUp?.(node.id, inputPort)}
          showValueInput={
            inputPort.valueType !== "list" && inputPort.valueType !== "object"
          }
          isConnected={isPortConnected?.(node.id, inputPort.id) ?? false}
          onValueChange={(value) =>
            onPortValueChange?.(node.id, inputPort.id, value)
          }
        />
        {showObjectFieldSelect ? (
          <FormControl
            size="small"
            variant="outlined"
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              minWidth: 132,
              "& .MuiInputBase-input": {
                py: 0.45,
                px: 0.75,
                fontSize: 12,
              },
            }}
          >
            <Select<string>
              value={
                node.objectFieldPath &&
                objectFieldOptions.some(
                  (option) => option.value === node.objectFieldPath,
                )
                  ? node.objectFieldPath
                  : (objectFieldOptions[0]?.value ?? "")
              }
              onChange={(event) =>
                onObjectFieldPathChange?.(node.id, event.target.value)
              }
            >
              {objectFieldOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: "0 8px",
          gap: 4,
          minWidth: showDatePartFormatSelect ? 168 : undefined,
        }}
      >
        <span
          style={{
            fontSize: showDatePartFormatSelect ? 22 : 30,
            fontWeight: 600,
            textTransform: "uppercase",
            color: "#333333",
            padding: showDatePartFormatSelect ? "0 4px" : "0 30px",
            textAlign: "center",
            lineHeight: 1.1,
          }}
        >
          {bpDico[node.operator]}
        </span>
        {showDatePartFormatSelect ? (
          <FormControl
            size="small"
            fullWidth
            variant="outlined"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <InputLabel id={datePartLabelId}>{formatLabels.label}</InputLabel>
            <Select<string>
              labelId={datePartLabelId}
              label={formatLabels.label}
              value={datePartSelectValue}
              onChange={handleDatePartFormatSelectChange}
            >
              {DATE_PART_TEXT_FORMAT_CHOICES.map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {datePartFormatChoiceLabel(choice, formatLabels)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </div>
      {isComparator && comparatorPortA && comparatorPortB ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: 6,
            paddingRight: 4,
          }}
        >
          <PortHandle
            setPortsMap={setPortsMap}
            node={node}
            key={comparatorPortA.id}
            port={comparatorPortA}
            onMouseDown={() => onPortMouseDown?.(node.id, comparatorPortA)}
            onMouseUp={() => onPortMouseUp?.(node.id, comparatorPortA)}
            isConnected={
              isPortConnected?.(node.id, comparatorPortA.id) ?? false
            }
            onValueChange={(value) =>
              onPortValueChange?.(node.id, comparatorPortA.id, value)
            }
          />
          <PortHandle
            setPortsMap={setPortsMap}
            node={node}
            key={comparatorPortB.id}
            port={comparatorPortB}
            onMouseDown={() => onPortMouseDown?.(node.id, comparatorPortB)}
            onMouseUp={() => onPortMouseUp?.(node.id, comparatorPortB)}
            isConnected={
              isPortConnected?.(node.id, comparatorPortB.id) ?? false
            }
            onValueChange={(value) =>
              onPortValueChange?.(node.id, comparatorPortB.id, value)
            }
          />
        </div>
      ) : outputPort ? (
        <div>
          <PortHandle
            setPortsMap={setPortsMap}
            node={node}
            key={outputPort.id}
            port={outputPort}
            onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
            isConnected={isPortConnected?.(node.id, outputPort.id) ?? false}
            onValueChange={(value) =>
              onPortValueChange?.(node.id, outputPort.id, value)
            }
          />
        </div>
      ) : null}
    </OperatorNodeCard>
  );
};

export const UnaryOperatorNode = React.memo(UnaryOperatorNodeComponent);
